/**
 * Accessibility utilities for CV Builder
 * Provides keyboard navigation, focus management, and screen reader support
 */

/**
 * Manages focus for keyboard navigation
 */
export class FocusManager {
  private static instance: FocusManager;
  private focusableElements: HTMLElement[] = [];
  private currentFocusIndex = -1;

  static getInstance(): FocusManager {
    if (!FocusManager.instance) {
      FocusManager.instance = new FocusManager();
    }
    return FocusManager.instance;
  }

  /**
   * Gets all focusable elements within a container
   */
  getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
  }

  /**
   * Sets up keyboard navigation for a container
   */
  setupKeyboardNavigation(container: HTMLElement): () => void {
    this.focusableElements = this.getFocusableElements(container);
    
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Tab':
          this.handleTabNavigation(event);
          break;
        case 'ArrowDown':
        case 'ArrowUp':
          if (event.target && this.isInNavigableGroup(event.target as HTMLElement)) {
            event.preventDefault();
            this.handleArrowNavigation(event.key === 'ArrowDown' ? 1 : -1);
          }
          break;
        case 'Home':
          if (event.ctrlKey && this.focusableElements.length > 0) {
            event.preventDefault();
            this.focusElement(0);
          }
          break;
        case 'End':
          if (event.ctrlKey && this.focusableElements.length > 0) {
            event.preventDefault();
            this.focusElement(this.focusableElements.length - 1);
          }
          break;
        case 'Escape':
          this.handleEscapeKey(event);
          break;
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    
    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }

  /**
   * Handles Tab navigation with proper focus trapping
   */
  private handleTabNavigation(event: KeyboardEvent): void {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab (backward)
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab (forward)
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Handles arrow key navigation within navigable groups
   */
  private handleArrowNavigation(direction: number): void {
    const currentIndex = this.focusableElements.findIndex(el => el === document.activeElement);
    if (currentIndex === -1) return;

    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < this.focusableElements.length) {
      this.focusElement(newIndex);
    }
  }

  /**
   * Focuses an element by index and updates current focus index
   */
  private focusElement(index: number): void {
    if (index >= 0 && index < this.focusableElements.length) {
      this.currentFocusIndex = index;
      this.focusableElements[index].focus();
    }
  }

  /**
   * Checks if an element is in a navigable group (like section buttons)
   */
  private isInNavigableGroup(element: HTMLElement): boolean {
    return element.closest('.cv-section-navigation') !== null ||
           element.closest('[role="tablist"]') !== null ||
           element.closest('[role="group"]') !== null;
  }

  /**
   * Handles Escape key for closing modals or canceling operations
   */
  private handleEscapeKey(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    
    // Close modals or alerts
    const modal = target.closest('.modal');
    const alert = target.closest('.alert-dismissible');
    
    if (modal) {
      const closeButton = modal.querySelector('.btn-close') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      }
    } else if (alert) {
      const closeButton = alert.querySelector('.btn-close') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      }
    }
  }

  /**
   * Announces a message to screen readers
   */
  announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Sets focus to the first error field in a form
   */
  focusFirstError(container: HTMLElement): boolean {
    const errorField = container.querySelector('.is-invalid, [aria-invalid="true"]') as HTMLElement;
    if (errorField) {
      errorField.focus();
      return true;
    }
    return false;
  }

  /**
   * Creates a skip link for keyboard users
   */
  createSkipLink(targetId: string, text: string): HTMLElement {
    const skipLink = document.createElement('a');
    skipLink.href = `#${targetId}`;
    skipLink.textContent = text;
    skipLink.className = 'skip-link sr-only-focusable';
    skipLink.style.cssText = `
      position: absolute;
      top: -40px;
      left: 6px;
      background: #000;
      color: #fff;
      padding: 8px;
      text-decoration: none;
      z-index: 9999;
      border-radius: 4px;
    `;
    
    skipLink.addEventListener('focus', () => {
      skipLink.style.top = '6px';
    });
    
    skipLink.addEventListener('blur', () => {
      skipLink.style.top = '-40px';
    });
    
    return skipLink;
  }
}

/**
 * Screen reader utilities
 */
export class ScreenReaderUtils {
  /**
   * Creates descriptive text for form fields
   */
  static createFieldDescription(fieldName: string, isRequired: boolean, format?: string): string {
    let description = fieldName;
    if (isRequired) {
      description += ', required';
    }
    if (format) {
      description += `, format: ${format}`;
    }
    return description;
  }

  /**
   * Creates progress announcement
   */
  static createProgressAnnouncement(current: number, total: number, sectionName?: string): string {
    const percentage = Math.round((current / total) * 100);
    let announcement = `Progress: ${current} of ${total} sections completed, ${percentage} percent`;
    if (sectionName) {
      announcement += `, currently on ${sectionName}`;
    }
    return announcement;
  }

  /**
   * Creates validation error announcement
   */
  static createValidationErrorAnnouncement(errors: string[]): string {
    if (errors.length === 0) return '';
    if (errors.length === 1) {
      return `Validation error: ${errors[0]}`;
    }
    return `${errors.length} validation errors: ${errors.join(', ')}`;
  }

  /**
   * Creates success announcement
   */
  static createSuccessAnnouncement(action: string): string {
    return `Success: ${action} completed successfully`;
  }

  /**
   * Creates loading announcement
   */
  static createLoadingAnnouncement(action: string): string {
    return `Loading: ${action} in progress, please wait`;
  }
}

/**
 * ARIA utilities for enhanced accessibility
 */
export class AriaUtils {
  /**
   * Sets up ARIA attributes for a form section
   */
  static setupFormSection(container: HTMLElement, sectionName: string): void {
    container.setAttribute('role', 'region');
    container.setAttribute('aria-labelledby', `${sectionName}-heading`);
    
    const heading = container.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading && !heading.id) {
      heading.id = `${sectionName}-heading`;
    }
  }

  /**
   * Sets up ARIA attributes for a progress indicator
   */
  static setupProgressIndicator(element: HTMLElement, current: number, total: number): void {
    element.setAttribute('role', 'progressbar');
    element.setAttribute('aria-valuenow', current.toString());
    element.setAttribute('aria-valuemin', '0');
    element.setAttribute('aria-valuemax', total.toString());
    element.setAttribute('aria-label', `Progress: ${current} of ${total} sections completed`);
  }

  /**
   * Sets up ARIA attributes for a tab list
   */
  static setupTabList(container: HTMLElement): void {
    container.setAttribute('role', 'tablist');
    
    const tabs = container.querySelectorAll('button, a');
    tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
  }

  /**
   * Updates tab selection state
   */
  static updateTabSelection(container: HTMLElement, selectedIndex: number): void {
    const tabs = container.querySelectorAll('[role="tab"]');
    tabs.forEach((tab, index) => {
      const isSelected = index === selectedIndex;
      tab.setAttribute('aria-selected', isSelected.toString());
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    });
  }

  /**
   * Sets up ARIA attributes for form validation
   */
  static setupFormValidation(field: HTMLElement, errorId?: string): void {
    if (errorId) {
      field.setAttribute('aria-describedby', errorId);
      field.setAttribute('aria-invalid', 'true');
    } else {
      field.removeAttribute('aria-describedby');
      field.setAttribute('aria-invalid', 'false');
    }
  }

  /**
   * Creates an error message element with proper ARIA attributes
   */
  static createErrorMessage(fieldId: string, message: string): HTMLElement {
    const errorElement = document.createElement('div');
    errorElement.id = `${fieldId}-error`;
    errorElement.className = 'text-danger';
    errorElement.setAttribute('role', 'alert');
    errorElement.setAttribute('aria-live', 'polite');
    errorElement.textContent = message;
    return errorElement;
  }
}

/**
 * High contrast and visual accessibility utilities
 */
export class VisualAccessibilityUtils {
  /**
   * Checks if user prefers reduced motion
   */
  static prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Checks if user prefers high contrast
   */
  static prefersHighContrast(): boolean {
    return window.matchMedia('(prefers-contrast: high)').matches;
  }

  /**
   * Applies focus styles that are visible and accessible
   */
  static applyAccessibleFocus(element: HTMLElement): void {
    element.style.outline = '2px solid #0336FF';
    element.style.outlineOffset = '2px';
    element.style.borderRadius = '4px';
  }

  /**
   * Removes custom focus styles
   */
  static removeAccessibleFocus(element: HTMLElement): void {
    element.style.outline = '';
    element.style.outlineOffset = '';
  }

  /**
   * Ensures minimum color contrast for text
   */
  static ensureColorContrast(element: HTMLElement, backgroundColor: string, textColor: string): void {
    // This is a simplified implementation
    // In a real application, you would use a color contrast library
    element.style.backgroundColor = backgroundColor;
    element.style.color = textColor;
  }
}

/**
 * Keyboard shortcuts manager
 */
export class KeyboardShortcuts {
  private shortcuts: Map<string, () => void> = new Map();

  /**
   * Registers a keyboard shortcut
   */
  register(key: string, callback: () => void, description?: string): void {
    this.shortcuts.set(key.toLowerCase(), callback);
  }

  /**
   * Handles keyboard events for shortcuts
   */
  handleKeyDown(event: KeyboardEvent): boolean {
    const key = this.getKeyString(event);
    const callback = this.shortcuts.get(key);
    
    if (callback) {
      event.preventDefault();
      callback();
      return true;
    }
    
    return false;
  }

  /**
   * Gets a string representation of the key combination
   */
  private getKeyString(event: KeyboardEvent): string {
    const parts: string[] = [];
    
    if (event.ctrlKey) parts.push('ctrl');
    if (event.altKey) parts.push('alt');
    if (event.shiftKey) parts.push('shift');
    if (event.metaKey) parts.push('meta');
    
    parts.push(event.key.toLowerCase());
    
    return parts.join('+');
  }

  /**
   * Creates a help dialog showing available shortcuts
   */
  createHelpDialog(): HTMLElement {
    const dialog = document.createElement('div');
    dialog.className = 'keyboard-shortcuts-help';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-labelledby', 'shortcuts-title');
    
    const title = document.createElement('h3');
    title.id = 'shortcuts-title';
    title.textContent = 'Keyboard Shortcuts';
    
    const list = document.createElement('ul');
    
    // Add common shortcuts
    const commonShortcuts = [
      { key: 'Tab', description: 'Navigate between form fields' },
      { key: 'Shift+Tab', description: 'Navigate backwards between form fields' },
      { key: 'Arrow Keys', description: 'Navigate between section buttons' },
      { key: 'Enter/Space', description: 'Activate buttons and links' },
      { key: 'Escape', description: 'Close dialogs and notifications' },
      { key: 'Ctrl+Home', description: 'Go to first focusable element' },
      { key: 'Ctrl+End', description: 'Go to last focusable element' }
    ];
    
    commonShortcuts.forEach(shortcut => {
      const item = document.createElement('li');
      item.innerHTML = `<strong>${shortcut.key}</strong>: ${shortcut.description}`;
      list.appendChild(item);
    });
    
    dialog.appendChild(title);
    dialog.appendChild(list);
    
    return dialog;
  }
}

// Export singleton instances
export const focusManager = FocusManager.getInstance();
export const keyboardShortcuts = new KeyboardShortcuts();